import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Article } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FAMILIES = ["GLOBAL_MARKET", "ASSET_MARKET", "REGULATION", "SECURITY", "INSTITUTIONAL", "RWA", "PROTOCOL", "SECTOR", "GENERAL"];
const ICONS = ["dollar", "trending", "hash", "building", "pie", "layers", "search", "bitcoin"];

type Fact = { value: string; label: string; icon: string };

const initForm = (a: any) => ({
  context: a.context_family_override || "auto",
  wtm: a.why_this_matters || "",
  suppressWtm: !!a.suppress_wtm,
  snapshot: (a.story_snapshot || []).map((f: any) => ({ value: f.value, label: f.label, icon: f.icon || "hash" })) as Fact[],
  suppressSnapshot: !!a.suppress_snapshot,
  pins: (a.story_thread_pins || []).join(", "),
  relatedPins: (a.related_pins || []).join(", "),
  premiumPick: a.premium_pick_slug || "",
  suppressGoDeeper: !!a.suppress_go_deeper,
});

export const ArticleIntelligenceDialog = ({ article, onClose, onSaved }: {
  article: Article | null; onClose: () => void; onSaved: () => void;
}) => {
  const [form, setForm] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(article ? initForm(article as any) : null); }, [article]);

  if (!article || !form) return null;
  const raw = article as any;

  const setFact = (i: number, patch: Partial<Fact>) =>
    setForm({ ...form, snapshot: form.snapshot.map((f: Fact, j: number) => (j === i ? { ...f, ...patch } : f)) });

  const save = async () => {
    const payload: any = {};
    const initCtx = raw.context_family_override || "auto";
    if (form.context !== initCtx) payload.context_family_override = form.context === "auto" ? null : form.context;
    if (form.wtm.trim() !== (raw.why_this_matters || "")) payload.why_this_matters = form.wtm.trim() || null;
    if (form.suppressWtm !== !!raw.suppress_wtm) payload.suppress_wtm = form.suppressWtm;
    if (form.suppressSnapshot !== !!raw.suppress_snapshot) payload.suppress_snapshot = form.suppressSnapshot;
    const initSnap = JSON.stringify(initForm(raw).snapshot);
    const curSnap = form.snapshot.filter((f: Fact) => f.value.trim() && f.label.trim());
    if (JSON.stringify(curSnap) !== initSnap) payload.story_snapshot = curSnap;
    const pins = form.pins.split(",").map((s: string) => s.trim()).filter(Boolean);
    if (JSON.stringify(pins) !== JSON.stringify(raw.story_thread_pins || [])) payload.story_thread_pins = pins;
    const rPins = form.relatedPins.split(",").map((s: string) => s.trim()).filter(Boolean);
    if (JSON.stringify(rPins) !== JSON.stringify(raw.related_pins || [])) payload.related_pins = rPins;
    if (form.premiumPick.trim() !== (raw.premium_pick_slug || "")) payload.premium_pick_slug = form.premiumPick.trim() || null;
    if (form.suppressGoDeeper !== !!raw.suppress_go_deeper) payload.suppress_go_deeper = form.suppressGoDeeper;
    if (!Object.keys(payload).length) { onClose(); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/articles/${article.id}/intelligence`, payload);
      toast.success("Intelligence overrides saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto sm:max-w-xl"
        data-testid="intelligence-dialog">
        <DialogHeader><DialogTitle className="font-heading">Article Intelligence</DialogTitle></DialogHeader>
        <p className="font-mono text-[11px] text-gray-500 -mt-2 line-clamp-1">{article.title}</p>
        <div className="space-y-5">
          <div>
            <Label>Context family override</Label>
            <Select value={form.context} onValueChange={(v) => setForm({ ...form, context: v })}>
              <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10" data-testid="intel-context-select">
                <SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                <SelectItem value="auto">Auto (router decides)</SelectItem>
                {FAMILIES.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Why This Matters</Label>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase text-gray-500">
                Suppress
                <Switch checked={form.suppressWtm} data-testid="intel-suppress-wtm"
                  onCheckedChange={(v) => setForm({ ...form, suppressWtm: v })} />
              </span>
            </div>
            <Textarea value={form.wtm} onChange={(e) => setForm({ ...form, wtm: e.target.value })}
              data-testid="intel-wtm-input" placeholder="Leave empty to keep auto-generated text hidden"
              className="mt-1 bg-[#1A1A1A] border-white/10 min-h-[90px]" />
            <p className="font-mono text-[10px] text-gray-600 mt-1">Editing locks this field against auto-regeneration.</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Story Snapshot facts</Label>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase text-gray-500">
                Suppress
                <Switch checked={form.suppressSnapshot} data-testid="intel-suppress-snapshot"
                  onCheckedChange={(v) => setForm({ ...form, suppressSnapshot: v })} />
              </span>
            </div>
            <div className="mt-2 space-y-2">
              {form.snapshot.map((f: Fact, i: number) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`intel-fact-${i}`}>
                  <Input value={f.value} maxLength={14} placeholder="$125M"
                    onChange={(e) => setFact(i, { value: e.target.value })}
                    className="bg-[#1A1A1A] border-white/10 w-[110px] font-mono text-sm" />
                  <Input value={f.label} maxLength={40} placeholder="Label (3-5 words)"
                    onChange={(e) => setFact(i, { label: e.target.value })}
                    className="bg-[#1A1A1A] border-white/10 flex-1 text-sm" />
                  <Select value={f.icon} onValueChange={(v) => setFact(i, { icon: v })}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      {ICONS.map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" className="text-gray-500 hover:text-red-400 h-8 w-8 shrink-0"
                    onClick={() => setForm({ ...form, snapshot: form.snapshot.filter((_: Fact, j: number) => j !== i) })}>
                    <Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              {form.snapshot.length < 4 && (
                <Button variant="outline" size="sm" data-testid="intel-add-fact"
                  onClick={() => setForm({ ...form, snapshot: [...form.snapshot, { value: "", label: "", icon: "hash" }] })}
                  className="border-white/15 bg-transparent text-gray-300 hover:text-white">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add fact
                </Button>
              )}
            </div>
          </div>

          <div>
            <Label>Follow the Story pins (comma-separated slugs, chronological)</Label>
            <Input value={form.pins} onChange={(e) => setForm({ ...form, pins: e.target.value })}
              data-testid="intel-pins-input" placeholder="earlier-story-slug, latest-update-slug"
              className="mt-1 bg-[#1A1A1A] border-white/10 font-mono text-xs" />
          </div>

          <div>
            <Label>Related Intelligence pins (comma-separated slugs, shown first)</Label>
            <Input value={form.relatedPins} onChange={(e) => setForm({ ...form, relatedPins: e.target.value })}
              data-testid="intel-related-pins-input" placeholder="pinned-related-slug"
              className="mt-1 bg-[#1A1A1A] border-white/10 font-mono text-xs" />
            <p className="font-mono text-[10px] text-gray-600 mt-1">Automatic selection fills remaining slots.</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Go Deeper premium pick (slug)</Label>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase text-gray-500">
                Suppress
                <Switch checked={form.suppressGoDeeper} data-testid="intel-suppress-go-deeper"
                  onCheckedChange={(v) => setForm({ ...form, suppressGoDeeper: v })} />
              </span>
            </div>
            <Input value={form.premiumPick} onChange={(e) => setForm({ ...form, premiumPick: e.target.value })}
              data-testid="intel-premium-pick-input" placeholder="premium-report-slug (empty = automatic)"
              className="mt-1 bg-[#1A1A1A] border-white/10 font-mono text-xs" />
          </div>

          <Button onClick={save} disabled={busy} data-testid="intel-save-button"
            className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            {busy ? "Saving…" : "Save intelligence overrides"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
